import React, { useState, useEffect } from 'react';
import { X, QrCode, Copy, CheckCircle2, Trash2, Loader2, Share2, Sparkles } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { api } from '../lib/api';
import { PROGRESSION } from '../data/progressionAutoEcole';
import { getPublicOrigin } from '../lib/publicUrl';

// Aplatit toutes les étapes M/O/P pour cocher facilement dans le formulaire
const ALL_STEPS = (() => {
  const out = [];
  (PROGRESSION || []).forEach((phase) => {
    (phase.steps || phase.etapes || []).forEach((s) => out.push({ id: s.id, label: s.label, phase: phase.phase || phase.titre }));
  });
  return out;
})();

const PHASES = Array.from(new Set(ALL_STEPS.map((s) => s.phase)));

const STATUS_CYCLE = [null, 'vu', 'acquis', 'a_revoir'];
const STATUS_META = {
  vu:       { label: 'Vu',       color: '#38bdf8', bg: 'bg-sky-500/15',       border: 'border-sky-500/50',       fg: 'text-sky-200',    dot: '🔵' },
  acquis:   { label: 'Acquis',   color: '#22c55e', bg: 'bg-emerald-500/15',   border: 'border-emerald-500/50',   fg: 'text-emerald-200', dot: '🟢' },
  a_revoir: { label: 'À revoir', color: '#f59e0b', bg: 'bg-amber-500/15',     border: 'border-amber-500/50',     fg: 'text-amber-200',  dot: '🟠' },
  a_travailler: { label: 'À travailler', color: '#38bdf8', bg: 'bg-sky-500/15', border: 'border-sky-500/50', fg: 'text-sky-200', dot: '📚' },
};

const stepLabel = (id) => { const s = ALL_STEPS.find((x) => x.id === id); return s ? `${s.id} — ${s.label}` : id; };

export default function FlashSeanceModal({ open, onClose, onCreated, initialStudentName = '', kind = 'bilan' }) {
  const isPrep = kind === 'prep';
  const [studentName, setStudentName] = useState('');
  const [notes, setNotes] = useState('');
  const [stepStatus, setStepStatus] = useState({});   // { stepId: 'vu' | 'acquis' | 'a_revoir' }
  const [duration, setDuration] = useState(30);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open && initialStudentName) setStudentName(initialStudentName);
  }, [open, initialStudentName]);

  const cycleStatus = (id) => {
    if (isPrep) {
      setStatusDirect(id, 'a_travailler');
      return;
    }
    setStepStatus((prev) => {
      const cur = prev[id] || null;
      const idx = STATUS_CYCLE.indexOf(cur);
      const nextVal = STATUS_CYCLE[(idx + 1) % STATUS_CYCLE.length];
      const n = { ...prev };
      if (nextVal === null) delete n[id]; else n[id] = nextVal;
      return n;
    });
  };

  const setStatusDirect = (id, status) => {
    setStepStatus((prev) => {
      const n = { ...prev };
      if (n[id] === status) delete n[id]; else n[id] = status;
      return n;
    });
  };

  const selectedCount = Object.keys(stepStatus).length;
  const countsByStatus = Object.values(stepStatus).reduce((acc, s) => { acc[s] = (acc[s] || 0) + 1; return acc; }, {});

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (selectedCount === 0) {
      setErr(isPrep ? 'Choisis au moins un thème à préparer.' : 'Coche au moins un cours vu aujourd\u2019hui.');
      return;
    }
    setCreating(true);
    try {
      const stepsArr = Object.entries(stepStatus).map(([id, status]) => ({ id, status }));
      const res = await api.flashSeanceCreate({
        student_name: studentName.trim() || null,
        steps: stepsArr,
        notes: notes.trim(),
        duration_days: duration,
        kind,
      });
      setCreated(res);
      if (onCreated) onCreated(res);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Erreur lors de la création.');
    } finally {
      setCreating(false);
    }
  };

  const publicUrl = created ? `${getPublicOrigin()}/s/${created.token}` : '';
  const shareText = created
    ? (isPrep
      ? `📚 Prochain cours${created.student_name ? ` — ${created.student_name}` : ''}\nThèmes à réviser avant la séance :\n${created.steps.map((s) => `• ${stepLabel(typeof s === 'string' ? s : s.id)}`).join('\n')}${created.notes ? `\n\nCe que j'attends de toi : ${created.notes}` : ''}\n\n👉 Détail + cours : ${publicUrl}`
      : `📋 Fiche de séance${created.student_name ? ` — ${created.student_name}` : ''}\nCours vus aujourd'hui :\n${created.steps.map((s) => `• ${stepLabel(typeof s === 'string' ? s : s.id)}`).join('\n')}${created.notes ? `\n\nNotes : ${created.notes}` : ''}\n\n👉 Récap + mini-QCM : ${publicUrl}`)
    : '';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) { /* silent */ }
  };

  const reset = () => {
    setCreated(null);
    setStudentName('');
    setNotes('');
    setStepStatus({});
    setErr('');
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center bg-black/70 backdrop-blur-sm overflow-y-auto p-3 sm:p-6" onClick={onClose} data-testid="flash-modal">
      <div className="relative w-full max-w-2xl bg-[#0f0f10] border border-white/10 rounded-2xl shadow-2xl my-6" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} data-testid="flash-close" className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 text-white/70 hover:text-white flex items-center justify-center z-10">
          <X size={18} />
        </button>

        <div className="p-5 sm:p-7">
          <div className="flex items-center gap-3 mb-1">
            <div className={`w-10 h-10 rounded-xl border flex items-center justify-center ${isPrep ? 'bg-sky-500/15 border-sky-500/40 text-sky-400' : 'bg-red-500/15 border-red-500/40 text-red-400'}`}>
              <Sparkles size={18} />
            </div>
            <div>
              <div className={`text-[10.5px] uppercase tracking-[0.22em] font-bold ${isPrep ? 'text-sky-400' : 'text-red-400'}`}>{isPrep ? 'Préparation du prochain cours' : 'Fiche de séance flash'}</div>
              <h3 className="font-serif-editorial text-white text-[22px] sm:text-[26px] leading-tight">{isPrep ? 'Donner les thèmes à réviser' : 'Partager le cours du jour'}</h3>
            </div>
          </div>
          <p className="text-white/60 text-[12.5px] leading-relaxed mt-2">
            {isPrep
              ? <>Choisis les thèmes qui seront travaillés au prochain cours : l&apos;élève sait quoi réviser et <span className="text-white">ce que tu attends de lui</span>. Partage par QR, SMS ou texte.</>
              : <>Génère un QR code à montrer à ton élève en fin de séance. Il scanne, il voit un récap des cours vus + un mini-QCM — <span className="text-white">sans avoir à créer de compte</span>.</>}
          </p>

          {!created ? (
            <form onSubmit={submit} className="mt-6 space-y-5" data-testid="flash-form">
              {/* Prénom élève */}
              <div>
                <label htmlFor="fs-name" className="text-white/60 text-[11px] font-mono-spec uppercase tracking-wider font-bold">Prénom de l&apos;élève (optionnel)</label>
                <input
                  id="fs-name"
                  data-testid="flash-student-name"
                  type="text"
                  value={studentName}
                  onChange={(e) => setStudentName(e.target.value)}
                  maxLength={60}
                  placeholder="Ex. : Léa"
                  className="mt-1.5 w-full h-11 rounded-lg bg-white/[0.03] border border-white/10 focus:border-red-500/60 outline-none px-3 text-white text-[14px]"
                />
              </div>

              {/* Cours vus avec 3 statuts */}
              <div>
                <label className="text-white/60 text-[11px] font-mono-spec uppercase tracking-wider font-bold">
                  {isPrep
                    ? <>Thèmes du prochain cours · <span className="text-sky-400">{selectedCount}</span> sélectionné{selectedCount > 1 ? 's' : ''}</>
                    : <>Compétences vues aujourd&apos;hui · <span className="text-red-400">{selectedCount}</span> ({countsByStatus.acquis || 0} acquis · {countsByStatus.vu || 0} vu · {countsByStatus.a_revoir || 0} à revoir)</>}
                </label>
                {!isPrep && (
                <div className="mt-1 flex flex-wrap gap-2 text-[10.5px] font-mono-spec text-white/50">
                  <span className="inline-flex items-center gap-1">🔵 Vu</span>
                  <span className="inline-flex items-center gap-1">🟢 Acquis</span>
                  <span className="inline-flex items-center gap-1">🟠 À revoir</span>
                  <span className="text-white/30">— Clique pour cycler, ou touche longue → sélection directe</span>
                </div>
                )}
                <div className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-white/10 bg-white/[0.02] p-2 space-y-3" data-testid="flash-steps-list">
                  {PHASES.map((phase) => (
                    <div key={phase}>
                      <div className="text-[10px] font-mono-spec uppercase tracking-widest text-white/40 px-1 mb-1.5">{phase}</div>
                      <div className="space-y-1">
                        {ALL_STEPS.filter((s) => s.phase === phase).map((s) => {
                          const cur = stepStatus[s.id] || null;
                          const meta = cur ? STATUS_META[cur] : null;
                          return (
                            <div
                              key={s.id}
                              data-testid={`flash-step-${s.id}`}
                              className={`flex items-center gap-2 rounded-md border transition-colors ${meta ? `${meta.bg} ${meta.border}` : 'bg-white/[0.03] border-white/10'}`}
                            >
                              <button
                                type="button"
                                onClick={() => cycleStatus(s.id)}
                                data-testid={`flash-step-${s.id}-cycle`}
                                className="flex-1 text-left px-2.5 py-1.5 text-[11.5px] leading-tight"
                                title={s.label + ' · click pour cycler'}
                              >
                                <span className="font-mono-spec font-bold text-white">{s.id}</span> <span className={meta ? meta.fg : 'text-white/60'}>{s.label.length > 34 ? s.label.slice(0, 32) + '…' : s.label}</span>
                              </button>
                              <div className="flex items-center pr-1 gap-0.5 shrink-0">
                                {(isPrep ? [] : ['vu', 'acquis', 'a_revoir']).map((st) => {
                                  const m = STATUS_META[st];
                                  const active = cur === st;
                                  return (
                                    <button
                                      key={st}
                                      type="button"
                                      onClick={() => setStatusDirect(s.id, st)}
                                      data-testid={`flash-step-${s.id}-${st}`}
                                      title={m.label}
                                      className={`h-6 w-6 rounded-full text-[11px] leading-none flex items-center justify-center border ${active ? `${m.bg} ${m.border} ring-1 ring-white/25` : 'bg-transparent border-white/10 hover:border-white/25'}`}
                                    >
                                      {m.dot}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label htmlFor="fs-notes" className="text-white/60 text-[11px] font-mono-spec uppercase tracking-wider font-bold">
                  {isPrep ? 'Ce que j\u2019attends de lui (optionnel · max 800 car.)' : 'Notes / points-clés (optionnel · max 800 car.)'}
                </label>
                <textarea
                  id="fs-notes"
                  data-testid="flash-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  maxLength={800}
                  rows={3}
                  placeholder={isPrep ? "Ex. : Je veux que tu maîtrises les priorités à droite et les giratoires. Revois aussi les distances de sécurité avant la séance." : 'Ex. : Belle progression sur la marche arrière. À retravailler : anticipation dans les giratoires + regard porté.'}
                  className="mt-1.5 w-full rounded-lg bg-white/[0.03] border border-white/10 focus:border-red-500/60 outline-none px-3 py-2.5 text-white text-[13px] leading-relaxed resize-none"
                />
                <div className="text-right text-white/40 text-[10.5px] font-mono-spec mt-0.5">{notes.length}/800</div>
              </div>

              {/* Durée */}
              <div>
                <label htmlFor="fs-dur" className="text-white/60 text-[11px] font-mono-spec uppercase tracking-wider font-bold">
                  Durée de validité du lien
                </label>
                <div className="mt-1.5 flex gap-2">
                  {[7, 30, 90].map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setDuration(d)}
                      data-testid={`flash-duration-${d}`}
                      className={`h-9 px-3.5 rounded-full text-[12.5px] font-semibold border transition-colors ${duration === d ? 'bg-red-500/15 border-red-500/50 text-white' : 'bg-white/[0.03] border-white/10 text-white/60 hover:bg-white/[0.06]'}`}
                    >
                      {d} j
                    </button>
                  ))}
                </div>
              </div>

              {err && (
                <div className="text-red-400 text-[13px] bg-red-500/10 border border-red-500/25 rounded-md p-2.5" data-testid="flash-error">{err}</div>
              )}

              <button
                type="submit"
                disabled={creating}
                data-testid="flash-submit"
                className={`w-full h-12 rounded-full disabled:opacity-50 text-white font-bold text-[14px] inline-flex items-center justify-center gap-2 shadow-lg ${isPrep ? 'bg-sky-600 hover:bg-sky-500 shadow-sky-500/25' : 'bg-red-600 hover:bg-red-500 shadow-red-500/25'}`}
              >
                {creating ? <><Loader2 size={16} className="animate-spin" /> Génération du QR…</> : <><QrCode size={16} /> {isPrep ? 'Générer le QR de préparation' : 'Générer le QR code'}</>}
              </button>
            </form>
          ) : (
            <div className="mt-6" data-testid="flash-created">
              <div className="text-center">
                <div className="inline-flex items-center gap-2 text-emerald-400 text-[12px] font-mono-spec font-bold bg-emerald-500/10 border border-emerald-500/30 rounded-full px-3 py-1">
                  <CheckCircle2 size={14} /> Fiche créée · valable {duration} jours
                </div>
              </div>

              {/* Intégration auto au parcours élève */}
              {created.auto_claimed && (
                <div className="mt-4 rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-3.5 flex items-start gap-2.5" data-testid="flash-auto-claimed">
                  <CheckCircle2 size={16} className="text-emerald-400 shrink-0 mt-0.5" />
                  <div className="text-[12.5px] leading-relaxed text-emerald-100">
                    <b>{isPrep ? `Envoyée à ${created.student_account_name}` : `Intégrée automatiquement au parcours de ${created.student_account_name}`}</b> — {isPrep ? 'les thèmes à réviser s\u2019affichent sur son tableau de bord et dans sa messagerie.' : 'sa progression est mise à jour et la fiche l\u2019attend dans sa messagerie.'} Le QR reste disponible si besoin.
                  </div>
                </div>
              )}
              {!created.auto_claimed && created.student_name && (
                <div className="mt-4 rounded-xl border border-amber-500/30 bg-amber-500/[0.07] p-3.5 text-[12.5px] leading-relaxed text-amber-100/90" data-testid="flash-no-account">
                  Aucun compte élève trouvé au nom de « {created.student_name} » — partage-lui le QR ou le lien ci-dessous, la fiche s&apos;intégrera à son suivi dès qu&apos;il l&apos;ouvrira connecté.
                </div>
              )}

              {/* QR code */}
              <div className="mt-5 mx-auto w-[220px] p-4 bg-white rounded-2xl shadow-xl" data-testid="flash-qr">
                <QRCodeSVG value={publicUrl} size={188} level="H" includeMargin={false} />
              </div>

              {/* URL */}
              <div className="mt-4">
                <label className="text-white/60 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Lien de partage</label>
                <div className="mt-1.5 flex gap-1.5">
                  <input
                    readOnly
                    value={publicUrl}
                    data-testid="flash-url"
                    onClick={(e) => e.target.select()}
                    className="flex-1 h-10 rounded-lg bg-white/[0.04] border border-white/10 outline-none px-3 text-white text-[12.5px] font-mono-spec"
                  />
                  <button
                    onClick={copy}
                    data-testid="flash-copy"
                    className="h-10 px-3 rounded-lg bg-white/10 hover:bg-white/15 text-white text-[12px] font-semibold inline-flex items-center gap-1.5"
                  >
                    {copied ? <><CheckCircle2 size={14} className="text-emerald-400" /> Copié</> : <><Copy size={14} /> Copier</>}
                  </button>
                </div>
              </div>

              {/* Partage : SMS / texte complet */}
              <div className="mt-3 grid grid-cols-2 gap-2">
                <a
                  href={`sms:?&body=${encodeURIComponent(shareText)}`}
                  data-testid="flash-sms"
                  className="h-11 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-[13px] font-bold inline-flex items-center justify-center gap-1.5"
                >
                  📱 Envoyer par SMS
                </a>
                <button
                  onClick={async () => { try { await navigator.clipboard.writeText(shareText); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (_) { /* silent */ } }}
                  data-testid="flash-copy-text"
                  className="h-11 rounded-full bg-white/10 hover:bg-white/15 text-white text-[13px] font-semibold inline-flex items-center justify-center gap-1.5"
                >
                  {copied ? <><CheckCircle2 size={14} className="text-emerald-400" /> Copié</> : <><Copy size={14} /> Copier le texte</>}
                </button>
              </div>

              {/* Récap */}
              <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.02] p-3 text-[12.5px] text-white/70 leading-relaxed">
                <div><b className="text-white">Élève :</b> {created.student_name || <span className="text-white/40">— (non spécifié)</span>}</div>
                <div className="mt-0.5"><b className="text-white">{isPrep ? 'Thèmes à préparer' : 'Cours vus'} ({created.steps.length}) :</b> {created.steps.map((s) => (typeof s === 'string' ? s : s.id)).join(', ')}</div>
                {created.notes && <div className="mt-0.5"><b className="text-white">{isPrep ? 'Attentes' : 'Notes'} :</b> <span className="italic">{created.notes}</span></div>}
              </div>

              {/* Actions */}
              <div className="mt-5 grid grid-cols-2 gap-2">
                <button
                  onClick={reset}
                  data-testid="flash-new"
                  className="h-11 rounded-full bg-white/10 hover:bg-white/15 text-white text-[13px] font-semibold"
                >
                  Nouvelle fiche
                </button>
                <a
                  href={publicUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="flash-preview"
                  className="h-11 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] font-bold inline-flex items-center justify-center gap-1.5"
                >
                  <Share2 size={14} /> Aperçu
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
